import {
  Box,
  Button,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
  Avatar,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import ApiServices from "../../config/ApiServices";
import ApiEndpoint from "../../config/ApiEndpoint";
import { toast } from "react-toastify";
import { PageHeader, Card, DataTable, StatusBadge, UserDetailsModal } from "../shared";

const SubOrgs = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tab, setTab] = useState(0);
  const [subOrgs, setSubOrgs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [adminSubOrgs, setAdminSubOrgs] = useState([]);
  const [allSubOrgs, setAllSubOrgs] = useState([]); // Store all sub orgs for filtering
  const [masterOrgsMap, setMasterOrgsMap] = useState({}); // Map of master_org_id to club partner data (name, logo_url, email)
  const [masterOrgsList, setMasterOrgsList] = useState([]); // List of club partners for filter dropdown
  const [selectedMasterOrg, setSelectedMasterOrg] = useState(""); // Selected club partner filter
  const [loading, setLoading] = useState(true);
  const [membersDialog, setMembersDialog] = useState({ open: false, subOrgId: null, subOrgName: "", members: [], loading: false });
  const [viewModal, setViewModal] = useState({ open: false, userId: null });

  const token = props?.props?.profile?.token;
  const userType =
    props?.props?.profile?.user_type || props?.props?.profile?.data?.user_type;
  const masterOrgId = props?.props?.profile?.master_org_id;

  const headers = {
    "Content-Type": "application/json",
    "x-access-token": token,
  };

  const loadAdminOrgs = async () => {
    if (!token) return;
    setLoading(true);
    const data = await ApiServices.GetApiCall(
      ApiEndpoint.ADMIN_ORGS_ALL,
      headers
    );
    setLoading(false);
    if (data?.status) {
      // Only show sub orgs, not club partners
      const subOrgs = data.data?.sub_orgs || [];
      setAllSubOrgs(subOrgs); // Store all sub orgs
      setAdminSubOrgs(subOrgs); // Initially show all
      
      // Create a mapping of master_org_id to club partner data (name, logo_url, email)
      // Backend now provides master_org_id in club partners response which matches sub-org's master_org_id
      const masterOrgs = data.data?.master_orgs || [];
      const map = {};
      const masterOrgsArray = [];
      masterOrgs.forEach(org => {
        // Use master_org_id as the key (this matches sub-org's master_org_id reference)
        const masterOrgId = org.master_org_id || org.id;
        if (masterOrgId) {
          const orgData = {
            id: masterOrgId, // Use master_org_id as the primary ID for matching
            name: org.name || org.organisation_name || "",
            logo_url: org.logo_url || "",
            email: org.email || org.user?.email || "",
          };
          // Store with both string and number keys to handle type mismatches
          map[masterOrgId] = orgData;
          map[String(masterOrgId)] = orgData;
          map[Number(masterOrgId)] = orgData;
          masterOrgsArray.push(orgData);
        }
      });
      setMasterOrgsMap(map);
      setMasterOrgsList(masterOrgsArray);
    } else if (data?.message) {
      toast.error(data.message);
    }
  };

  const loadMasterSubOrgs = async () => {
    if (!token) return;
    setLoading(true);
    const data = await ApiServices.GetApiCall(
      ApiEndpoint.MASTER_ORG_SUB_ORGS,
      headers
    );
    setLoading(false);
    if (data?.status) {
      setSubOrgs(data.data || []);
    } else if (data?.message) {
      toast.error(data.message);
    }
  };

  const loadRequests = async () => {
    if (!token) return;
    const data = await ApiServices.GetApiCall(
      `${ApiEndpoint.MASTER_ORG_REQUESTS}?status=pending`,
      headers
    );
    if (data?.status) {
      setRequests(data.data || []);
    } else if (data?.message) {
      toast.error(data.message);
    }
  };

  const loadAnalytics = async () => {
    if (!token) return;
    const data = await ApiServices.GetApiCall(
      ApiEndpoint.MASTER_ORG_ANALYTICS_DASHBOARD,
      headers
    );
    if (data?.status) {
      setAnalytics(data.data || {});
    }
  };

  // Filter sub orgs by selected club partner
  useEffect(() => {
    if (userType === "admin" && allSubOrgs.length > 0) {
      if (selectedMasterOrg) {
        const filtered = allSubOrgs.filter(org => {
          // Sub orgs now have master_org_id that matches club partner's master_org_id
          const orgMasterId = org.master_org_id;
          // Compare as both string and number
          return orgMasterId && (
            String(orgMasterId) === String(selectedMasterOrg) || 
            Number(orgMasterId) === Number(selectedMasterOrg)
          );
        });
        setAdminSubOrgs(filtered);
      } else {
        // Show all if no filter selected
        setAdminSubOrgs(allSubOrgs);
      }
    }
  }, [selectedMasterOrg, allSubOrgs, userType]);

  useEffect(() => {
    if (!token) return;
    if (userType === "admin") {
      loadAdminOrgs();
    } else if (userType === "master_org") {
      loadMasterSubOrgs();
      loadRequests();
      loadAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userType, masterOrgId]);

  const handleRequestAction = async (requestId, action) => {
    if (!token) return;
    const endpoint =
      action === "approve"
        ? ApiEndpoint.MASTER_ORG_REQUEST_APPROVE
        : ApiEndpoint.MASTER_ORG_REQUEST_DENY;
    props.setIsLoaded(true);
    const data = await ApiServices.PostApiCall(
      endpoint,
      JSON.stringify({ request_id: requestId }),
      headers
    );
    props.setIsLoaded(false);
    if (data?.status) {
      toast.success(data.message || "Request updated");
      loadRequests();
      loadMasterSubOrgs();
    } else {
      toast.error(data?.message || "Failed to update request");
    }
  };

  // Fetch members for a sub-org (admin view)
  const fetchSubOrgMembers = async (subOrgId, subOrgName) => {
    if (!token || !subOrgId) return;
    
    setMembersDialog({ 
      open: true, 
      subOrgId, 
      subOrgName, 
      members: [], 
      loading: true 
    });
    
    try {
      // Backend now supports sub_org_id filtering - no client-side filtering needed
      const requestBody = {
        type: "user",
        pagination: false, // Get all members - backend returns all when pagination: false
        sub_org_id: subOrgId, // Backend filters users by sub-org ID
        status: "active",
      };
      
      // Debug: Log request
      if (process.env.NODE_ENV === 'development') {
        console.log(`Fetching members for sub-org ${subOrgName} (ID: ${subOrgId}):`, requestBody);
      }
      
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.ADMIN_USER_LIST,
        JSON.stringify(requestBody),
        headers
      );
      
      if (data?.status) {
        const members = Array.isArray(data.data) ? data.data : [];
        
        // Backend already filters by sub_org_id and excludes organizations
        // Just filter out organizations as a safety net (backend should handle this, but keep as fallback)
        const usersOnly = members.filter(
          (item) =>
            !["schools", "individual", "master_org", "sub_org", "master"].includes(item.role) &&
            item.status === "active"
        );
        
        // Debug: Log response
        if (process.env.NODE_ENV === 'development') {
          console.log(`Sub-org ${subOrgName} (ID: ${subOrgId}) members response:`, {
            totalFromAPI: members.length,
            usersOnly: usersOnly.length,
            members: usersOnly.map(m => ({ 
              id: m.id, 
              name: `${m.first_name} ${m.last_name}`, 
              email: m.email,
              role: m.role
            })),
            rawResponse: data,
          });
        }
        
        setMembersDialog(prev => ({ 
          ...prev, 
          members: usersOnly, 
          loading: false 
        }));
      } else {
        toast.error(data?.message || "Failed to load members");
        setMembersDialog(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error("Failed to fetch sub-org members:", error);
      toast.error("Failed to load members");
      setMembersDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Table columns for admin sub orgs
  const adminColumns = [
    {
      field: "id",
      label: "ID",
      width: 70,
    },
    {
      field: "name",
      label: "Club",
      render: (_, row) => {
        // Get logo URL directly from API response (backend now includes logo_url)
        const logoUrl = row.logo_url || "";
        const orgName = row.name || row.organisation_name || "";
        
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              src={logoUrl}
              sx={{
                width: 40,
                height: 40,
                bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                color: isDark ? "#818cf8" : "#1976d2",
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
            >
              {orgName[0]?.toUpperCase() || "O"}
            </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", fontSize: "0.875rem" }}>
              {orgName || "-"}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#718096" : "#718096" }}>
              {row.email || row.user?.email || "-"}
            </Typography>
          </Box>
        </Box>
        );
      },
    },
    {
      field: "master_org",
      label: "Club Partner",
      mobileSameLineGroup: "meta",
      render: (_, row) => {
        // Sub orgs now have master_org_id that directly matches club partner's master_org_id
        const masterOrgId = row.master_org_id;
        
        // Get club partner data from map using master_org_id (try both string and number keys)
        let masterOrgData = masterOrgId ? (
          masterOrgsMap[masterOrgId] || 
          masterOrgsMap[String(masterOrgId)] || 
          masterOrgsMap[Number(masterOrgId)]
        ) : null;
        
        // Fallback: check if club partner data is embedded in the row
        if (!masterOrgData && row.master_org) {
          masterOrgData = {
            name: row.master_org.name || row.master_org.organisation_name || "",
            logo_url: row.master_org.logo_url || "",
            email: row.master_org.email || row.master_org.user?.email || "",
          };
        }
        
        const masterOrgName = masterOrgData?.name || 
                             (masterOrgId ? `ID: ${masterOrgId}` : "N/A");
        const logoUrl = masterOrgData?.logo_url || "";
        const email = masterOrgData?.email || "";
        
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              src={logoUrl}
              sx={{
                width: 40,
                height: 40,
                bgcolor: isDark ? "rgba(139, 92, 246, 0.15)" : "rgba(156, 39, 176, 0.1)",
                color: isDark ? "#a78bfa" : "#9c27b0",
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
            >
              {masterOrgName[0]?.toUpperCase() || "M"}
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", fontSize: "0.875rem" }}>
                {masterOrgName}
              </Typography>
              {email && (
                <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#718096" : "#718096" }}>
                  {email}
                </Typography>
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "members",
      label: "Members",
      width: 200,
      mobileSameLineGroup: "meta",
      render: (_, row) => {
        // Prefer detailed members array if backend provides it
        let users = row.members || row.users || [];

        // Handle case where it might be a single object (backward compatibility)
        if (!Array.isArray(users)) {
          if (users && typeof users === "object" && Object.keys(users).length > 0) {
            users = [users];
          } else {
            users = [];
          }
        }

        // Remove duplicates based on user ID
        const uniqueUsers = [];
        const seenIds = new Set();

        users.forEach((user) => {
          const status = user.status || user.user?.status;
          if (status && status !== "active") return;

          const userId = user.id || user.user_id || user.id_user;
          const firstName = user.first_name || user.name || "";
          const lastName = user.last_name || "";

          if (firstName || lastName || userId) {
            if (userId && !seenIds.has(userId)) {
              seenIds.add(userId);
              uniqueUsers.push(user);
            } else if (!userId) {
              const nameExists = uniqueUsers.some(
                (u) =>
                  (u.first_name || u.name) === firstName &&
                  (u.last_name || "") === lastName &&
                  (u.email || u.user?.email) === (user.email || user.user?.email)
              );
              if (!nameExists) {
                uniqueUsers.push(user);
              }
            }
          }
        });

        // If no user data, show "-" but still allow click to open full members dialog
        if (uniqueUsers.length === 0) {
          const membersCount =
            row.active_members_count ||
            row.members_count ||
            row.member_count ||
            row.total_members ||
            0;
          return (
            <Typography
              onClick={() => fetchSubOrgMembers(row.id, row.name)}
              sx={{
                color: isDark ? "#818cf8" : "#1976d2",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                  color: isDark ? "#a5b4fc" : "#1565c0",
                },
              }}
            >
              {membersCount > 0 ? `${membersCount} active members` : "-"}
            </Typography>
          );
        }

        // Display avatars in a row with "+X" indicator (max 2 avatars, then "+X")
        const maxVisible = 2;
        const visibleUsers = uniqueUsers.slice(0, maxVisible);
        const remainingCount = uniqueUsers.length - maxVisible;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
            }}
            onClick={() => fetchSubOrgMembers(row.id, row.name)}
          >
            {visibleUsers.map((user, index) => {
              const profileImage = user.profile_image || user.user?.profile_image || "";
              const firstName = user.first_name || user.name || "";
              const lastName = user.last_name || "";
              const initials = `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase() || "U";

              return (
                <Avatar
                  key={user.id || user.user_id || index}
                  src={profileImage}
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                    color: isDark ? "#818cf8" : "#1976d2",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    border: isDark ? "2px solid rgba(255, 255, 255, 0.1)" : "2px solid #ffffff",
                    marginLeft: index > 0 ? "-8px" : 0,
                    zIndex: maxVisible - index,
                    "&:hover": {
                      zIndex: 10,
                      transform: "scale(1.1)",
                      transition: "all 0.2s ease",
                    },
                  }}
                >
                  {initials}
                </Avatar>
              );
            })}
            {remainingCount > 0 && (
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: isDark ? "rgba(99, 102, 241, 0.2)" : "rgba(25, 118, 210, 0.15)",
                  color: isDark ? "#818cf8" : "#1976d2",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  border: isDark ? "2px solid rgba(255, 255, 255, 0.1)" : "2px solid #ffffff",
                  marginLeft: "-8px",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: isDark ? "rgba(99, 102, 241, 0.3)" : "rgba(25, 118, 210, 0.25)",
                    transform: "scale(1.1)",
                    transition: "all 0.2s ease",
                  },
                }}
              >
                +{remainingCount}
              </Avatar>
            )}
          </Box>
        );
      },
    },
    {
      field: "status",
      label: "Status",
      align: "right",
      mobileSameLineGroup: "meta",
      render: (value, row) => {
        const connectionStatus = value || row.connection_status || "approved";
        const userStatus = row.user?.status || "active";
        const userRole = row.user?.role || "";
        
        // Check for posting issues
        const canPost = 
          connectionStatus === "approved" && 
          userStatus === "active" && 
          userRole === "schools";
        
        const hasMismatch = 
          connectionStatus === "approved" && 
          (userStatus !== "active" || userRole !== "schools");
        
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <StatusBadge status={connectionStatus} />
            {hasMismatch && (
              <Tooltip 
                title={
                  <Box>
                    <Typography variant="caption" display="block">
                      <strong>Posting Issue Detected:</strong>
                    </Typography>
                    {userStatus !== "active" && (
                      <Typography variant="caption" display="block">
                        • User status: {userStatus} (needs: active)
                      </Typography>
                    )}
                    {userRole !== "schools" && (
                      <Typography variant="caption" display="block">
                        • User role: {userRole} (needs: schools)
                      </Typography>
                    )}
                    <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                      This club cannot create posts until fixed.
                    </Typography>
                  </Box>
                }
                arrow
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: '4px',
                    backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fee2e2',
                    border: `1px solid ${isDark ? 'rgba(239, 68, 68, 0.3)' : '#fecaca'}`,
                    cursor: 'help',
                  }}
                >
                  <Typography 
                    sx={{ 
                      fontSize: '0.65rem', 
                      color: '#ef4444',
                      fontWeight: 600,
                    }}
                  >
                    ⚠️ Cannot Post
                  </Typography>
                </Box>
              </Tooltip>
            )}
            {canPost && (
              <Tooltip title="This club can create posts. All requirements met." arrow>
                <Typography 
                  sx={{ 
                    fontSize: '0.65rem', 
                    color: isDark ? '#84cc16' : '#65a30d',
                    fontStyle: 'italic',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  ✓ Ready to post
                </Typography>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  // Table columns for club partner sub orgs
  const masterColumns = [
    {
      field: "id",
      label: "ID",
      width: 70,
    },
    {
      field: "name",
      label: "Club",
      render: (_, row) => {
        // Get logo URL directly from API response (backend now includes logo_url)
        const logoUrl = row.logo_url || "";
        const orgName = row.name || "";
        
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              src={logoUrl}
              sx={{
                width: 40,
                height: 40,
                bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                color: isDark ? "#818cf8" : "#1976d2",
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
            >
              {orgName[0]?.toUpperCase() || "O"}
            </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", fontSize: "0.875rem" }}>
              {row.name || "-"}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#718096" : "#718096" }}>
              {row.user?.email || "-"}
            </Typography>
          </Box>
        </Box>
        );
      },
    },
    {
      field: "status",
      label: "Status",
      align: "right",
      mobileSameLineGroup: "meta",
      render: (value, row) => {
        const connectionStatus = value || "approved";
        const userStatus = row.user?.status || row.status || "active";
        
        // Show connection status badge
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <StatusBadge status={connectionStatus} />
            {connectionStatus === "approved" && userStatus !== "active" && (
              <Typography 
                sx={{ 
                  fontSize: '0.7rem', 
                  color: '#ef4444',
                  fontStyle: 'italic'
                }}
              >
                User: {userStatus}
              </Typography>
            )}
          </Box>
        );
      },
    },
  ];

  // Table columns for join requests
  const requestColumns = [
    {
      field: "id",
      label: "ID",
      width: 70,
    },
    {
      field: "name",
      label: "Club",
      render: (_, row) => {
        // Get logo URL directly from API response (backend now includes logo_url)
        const subOrg = row.subOrg || {};
        const logoUrl = subOrg.logo_url || "";
        const orgName = subOrg.name || "";
        
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              src={logoUrl}
              sx={{
                width: 40,
                height: 40,
                bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                color: isDark ? "#818cf8" : "#1976d2",
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
            >
              {orgName[0]?.toUpperCase() || "O"}
            </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", fontSize: "0.875rem" }}>
              {subOrg.name || "-"}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#718096" : "#718096" }}>
              {subOrg.user?.email || "-"}
            </Typography>
          </Box>
        </Box>
        );
      },
    },
    {
      field: "status",
      label: "Status",
      align: "right",
      mobileSameLineGroup: "meta",
      render: (value) => <StatusBadge status={value || "pending"} />,
    },
    {
      field: "actions",
      label: "Actions",
      render: (_, row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-end" }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleRequestAction(row.id, "deny")}
            sx={{
              borderColor: isDark ? "rgba(255, 255, 255, 0.2)" : "#e2e8f0",
              color: isDark ? "#ffffff" : "#1a202c",
              "&:hover": {
                borderColor: "#ef4444",
                backgroundColor: isDark ? "rgba(239, 68, 68, 0.1)" : "#fef2f2",
                color: "#ef4444",
              },
            }}
          >
            Deny
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => handleRequestAction(row.id, "approve")}
            sx={{
              backgroundColor: isDark ? "#6366f1" : "#1976d2",
              "&:hover": {
                backgroundColor: isDark ? "#4f46e5" : "#1565c0",
              },
            }}
          >
            Approve
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title={userType === "admin" ? "Connected Clubs" : "Clubs"}
        subtitle="View clubs, analytics, and join requests."
        breadcrumbs={userType === "admin" ? [{ label: "Admin" }, { label: "Connected Clubs" }] : [{ label: "Club Partner" }, { label: "Clubs" }]}
      />

      {userType === "admin" ? (
        <>
          {/* Club Partner Filter */}
          <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              select
              label="Filter by Club Partner"
              value={selectedMasterOrg}
              onChange={(e) => setSelectedMasterOrg(e.target.value)}
              sx={{
                minWidth: 300,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: isDark ? "#242424" : "#f7fafc",
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0",
                  },
                  "&:hover fieldset": {
                    borderColor: isDark ? "#6366f1" : "#1976d2",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: isDark ? "#818cf8" : "#1976d2",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: isDark ? "#cbd5e1" : "#718096",
                },
                "& .MuiInputBase-input": {
                  color: isDark ? "#ffffff" : "#1a202c",
                },
              }}
            >
              <MenuItem value="">
                <Typography sx={{ color: isDark ? "#ffffff" : "#1a202c" }}>
                  All Club Partners
                </Typography>
              </MenuItem>
              {masterOrgsList.map((masterOrg) => (
                <MenuItem key={masterOrg.id} value={masterOrg.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar
                      src={masterOrg.logo_url}
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: isDark ? "rgba(139, 92, 246, 0.15)" : "rgba(156, 39, 176, 0.1)",
                        color: isDark ? "#a78bfa" : "#9c27b0",
                        fontSize: "0.75rem",
                      }}
                    >
                      {masterOrg.name[0]?.toUpperCase() || "M"}
                    </Avatar>
                    <Typography sx={{ color: isDark ? "#ffffff" : "#1a202c" }}>
                      {masterOrg.name}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            {selectedMasterOrg && (
              <Chip
                label={`Showing clubs for: ${masterOrgsMap[selectedMasterOrg]?.name || masterOrgsMap[String(selectedMasterOrg)]?.name || "Selected Club Partner"}`}
                onDelete={() => setSelectedMasterOrg("")}
                sx={{
                  backgroundColor: isDark ? "rgba(139, 92, 246, 0.15)" : "rgba(156, 39, 176, 0.1)",
                  color: isDark ? "#a78bfa" : "#9c27b0",
                  "& .MuiChip-deleteIcon": {
                    color: isDark ? "#a78bfa" : "#9c27b0",
                    "&:hover": { color: isDark ? "#c4b5fd" : "#7b1fa2" },
                  },
                }}
              />
            )}
          </Box>
          <Card noPadding>
            <DataTable
              columns={adminColumns}
              data={adminSubOrgs}
              loading={loading}
              emptyMessage={selectedMasterOrg ? "No clubs found for selected club partner" : "No clubs found"}
              mobileTitleColumn="name"
              mobileHiddenColumns={["id"]}
              onRowClick={(row) => setViewModal({ open: true, userId: row.id })}
            />
          </Card>
        </>
      ) : (
        <>
          <Tabs
            value={tab}
            onChange={(event, newValue) => setTab(newValue)}
            sx={{
              mb: 2,
              borderBottom: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
            }}
          >
            <Tab label="Clubs" sx={{ color: isDark ? "#ffffff" : "#1a202c" }} />
            <Tab label="Join Requests" sx={{ color: isDark ? "#ffffff" : "#1a202c" }} />
            <Tab label="Analytics" sx={{ color: isDark ? "#ffffff" : "#1a202c" }} />
          </Tabs>

          {tab === 0 && userType === "admin" && (
            <Card noPadding>
              <DataTable
                columns={adminColumns}
                data={adminSubOrgs}
                loading={loading}
                emptyMessage="No clubs found"
                mobileTitleColumn="name"
                mobileHiddenColumns={["id"]}
              />
            </Card>
          )}

          {tab === 0 && userType === "master_org" && (
            <Card noPadding>
              <DataTable
                columns={masterColumns}
                data={subOrgs}
                loading={loading}
                emptyMessage="No clubs found"
                mobileTitleColumn="name"
                mobileHiddenColumns={["id"]}
                onRowClick={(row) => setViewModal({ open: true, userId: row.id })}
              />
            </Card>
          )}

          {tab === 1 && (
            <Card noPadding>
              <DataTable
                columns={requestColumns}
                data={requests}
                loading={false}
                emptyMessage="No pending requests"
                mobileTitleColumn="name"
                mobileHiddenColumns={["id"]}
              />
            </Card>
          )}

          {tab === 2 && (
            <Card>
              {!analytics ? (
                <Typography sx={{ color: isDark ? "#cbd5e1" : "#6f6f6f", textAlign: "center", py: 4 }}>
                  Analytics unavailable.
                </Typography>
              ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 2 }}>
                  {Object.entries(analytics).map(([key, value]) => (
                    <Box
                      key={key}
                      sx={{
                        backgroundColor: isDark ? "#242424" : "#f7fafc",
                        padding: 2,
                        borderRadius: "12px",
                        border: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid #e2e8f0",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: isDark ? "#cbd5e1" : "#7a7a7a",
                          mb: 1,
                          textTransform: "capitalize",
                        }}
                      >
                        {key.replace(/_/g, " ")}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.25rem",
                          fontWeight: 600,
                          color: isDark ? "#ffffff" : "#1c1c1c",
                        }}
                      >
                        {String(value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Card>
          )}
        </>
      )}

      {/* Sub Org View Modal */}
      <UserDetailsModal
        open={viewModal.open}
        onClose={() => setViewModal({ open: false, userId: null })}
        userId={viewModal.userId}
        role="schools"
        token={token}
        setIsLoaded={props.setIsLoaded}
      />

      {/* Members Dialog */}
      <Dialog
        open={membersDialog.open}
        onClose={() => setMembersDialog({ open: false, subOrgId: null, subOrgName: "", members: [], loading: false })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            borderRadius: "16px",
            margin: { xs: 2, sm: 2 },
            maxHeight: "85vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            borderBottom: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
          }}
        >
          <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: isDark ? "#ffffff" : "#1a202c" }}>
            Members - {membersDialog.subOrgName}
          </Typography>
          <IconButton
            onClick={() => setMembersDialog({ open: false, subOrgId: null, subOrgName: "", members: [], loading: false })}
            sx={{ color: isDark ? "#cbd5e1" : "#718096" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, px: 3, maxHeight: "70vh", overflowY: "auto" }}>
          {membersDialog.loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Typography sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>Loading members...</Typography>
            </Box>
          ) : membersDialog.members.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Typography sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>No members found</Typography>
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              {membersDialog.members.map((member, index) => (
                <Box
                  key={member.id || index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 2,
                    mb: 1,
                    borderRadius: "8px",
                    backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "#f7fafc",
                    border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
                  }}
                >
                  <Avatar
                    src={member.profile_image || ""}
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                      color: isDark ? "#818cf8" : "#1976d2",
                      fontSize: "1rem",
                      fontWeight: 600,
                    }}
                  >
                    {member.first_name?.[0] || ""}
                    {member.last_name?.[0] || ""}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", fontSize: "0.875rem" }}>
                      {member.first_name || ""} {member.last_name || ""}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#718096" : "#718096" }}>
                      {member.email || "-"}
                    </Typography>
                    {member.role && (
                      <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#cbd5e1" : "#718096", mt: 0.5 }}>
                        Role: {member.role}
                      </Typography>
                    )}
                  </Box>
                  {member.status && (
                    <StatusBadge status={member.status} />
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0" }}>
          <Button
            onClick={() => setMembersDialog({ open: false, subOrgId: null, subOrgName: "", members: [], loading: false })}
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubOrgs;
